import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import moment from 'moment';
import numeral from 'numeral';

import { Button, Form, Alert, Spinner, Dropdown } from 'react-bootstrap';

import styled from 'styled-components';

import {
    Container, CenteredTD, Header, HeaderActions, Panel, Toolbar, ToolbarFilters, SearchBox, ClearButton,
    FilterSelect, GhostButton, TableScroll, ListTable, Chip, StatusDot, Pager,
} from '../ManageLayout';

import { faTimes, faDownload, faArrowDown, faArrowUp, faColumns, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import UserProfile from '../../UserProfile';

const BASE_URL = `${process.env.REACT_APP_SERVER}geo-service/report/suppliers`;

const LOCKED_MESSAGE = 'Os dados estão sendo atualizados pelo processamento trimestral; tente novamente mais tarde.';

// Guarda os anos ocultos (e não os visíveis) para que anos novos apareçam por padrão.
const HIDDEN_YEARS_KEY = 'supplierReportHiddenYears';

function loadHiddenYears() {
    try {
        const stored = JSON.parse(localStorage.getItem(HIDDEN_YEARS_KEY));
        return Array.isArray(stored) ? stored : [];
    } catch (e) {
        return [];
    }
}

function saveHiddenYears(hiddenYears) {
    try {
        localStorage.setItem(HIDDEN_YEARS_KEY, JSON.stringify(hiddenYears));
    } catch (e) {
        console.log(e);
    }
}

const emptyFilters = {
    name: false,
    assoc: false,
    micro: false,
    risco: false,
};

function buildQuery(params) {
    let query = [];
    for (let key of Object.keys(params)) {
        if (!!params[key]) query.push(`${key}=${encodeURIComponent(params[key])}`);
    }

    return query.join('&');
}

function getErrorMessage(error) {
    if (error.response && error.response.status === 503) return LOCKED_MESSAGE;
    return 'Não foi possível carregar o relatório de fornecedores.';
}

function formatNumber(value) {
    return numeral(value || 0).format('0,0.00');
}

function SupplierReport() {
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const [options, setOptions] = useState({ assocs: [], micros: [] });
    const [lastUpdate, setLastUpdate] = useState(null);

    const [hiddenYears, setHiddenYears] = useState(loadHiddenYears);

    const [name, _name] = useState('');
    const timer = useRef(null);

    const [params, setParams] = useState({
        page: 1,
        filters: emptyFilters,
        order: sessionStorage.getItem('supplierReportOrder') || 'nome',
        direction: sessionStorage.getItem('supplierReportDirection') || 'asc',
    });

    useEffect(() => {
        async function fetchOptions() {
            try {
                const { data } = await axios.get(`${BASE_URL}/options`);
                setOptions(data);
            } catch (e) {
                console.log(e);
            }
        }

        async function fetchLastUpdate() {
            const { data } = await axios.get(`${process.env.REACT_APP_SERVER}geo-service/config/last_db_update`);
            if (data && data['last_db_update']) setLastUpdate(moment(data['last_db_update']).format('DD/MM/YYYY'));
        }

        fetchOptions();
        fetchLastUpdate();

        return () => clearTimeout(timer.current);
    }, []);

    useEffect(() => {
        let ignore = false;

        async function fetchReport() {
            setError(null);

            try {
                const { page, filters, order, direction } = params;
                const { data } = await axios.get(`${BASE_URL}?${buildQuery({ page, ...filters, order, direction })}`);

                if (!ignore) setReport(data);
            } catch (e) {
                if (!ignore) {
                    setReport(null);
                    setError(getErrorMessage(e));
                }
            }
        }

        fetchReport();

        return () => { ignore = true; };
    }, [params]);

    const handleFilter = (filterName, value) => {
        setParams((current) => ({
            ...current,
            page: 1,
            filters: { ...current.filters, [filterName]: value },
        }));
    }

    const handleNameKey = (e) => {
        clearTimeout(timer.current);

        const value = e.target.value;
        if (e.key === 'Enter') handleFilter('name', value);
        else timer.current = setTimeout(() => handleFilter('name', value), 1000); // debounce
    }

    const clearName = () => {
        clearTimeout(timer.current);
        _name('');
        handleFilter('name', false);
    }

    const handlePageChange = (page) => {
        setParams((current) => ({ ...current, page }));
    }

    const handleChangeOrder = (field) => {
        setParams((current) => {
            const direction = field === current.order
                ? (current.direction === 'asc' ? 'desc' : 'asc')
                : (field === 'nome' || field === 'associacao' || field === 'microrregiao' || field === 'municipio' ? 'asc' : 'desc');

            sessionStorage.setItem('supplierReportOrder', field);
            sessionStorage.setItem('supplierReportDirection', direction);

            return { ...current, page: 1, order: field, direction };
        });
    }

    const updateHiddenYears = (next) => {
        saveHiddenYears(next);
        setHiddenYears(next);
    }

    const toggleYear = (ano) => {
        updateHiddenYears(hiddenYears.includes(ano)
            ? hiddenYears.filter((hidden) => hidden !== ano)
            : [...hiddenYears, ano]);
    }

    const handleExport = async () => {
        setExporting(true);

        try {
            const { filters, order, direction } = params;
            const url = `${BASE_URL}/export?${buildQuery({ ...filters, order, direction })}`;
            const { data } = await axios.get(url, { responseType: 'blob' });
            const downloadUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');

            link.href = downloadUrl;
            link.setAttribute('download', 'relatorio_fornecedores.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            setError(getErrorMessage(e));
        }

        setExporting(false);
    }

    const years = report ? report.years : [];
    const visibleYears = years.filter((ano) => !hiddenYears.includes(ano));
    const riskWindow = report && report.riskWindow;
    const summary = report && report.summary;

    // o backend já restringe o usuário de associação aos próprios fornecedores; aqui só ajusta a tela
    const isADM = UserProfile.isADM;
    const userAssoc = !isADM && options.assocs.length === 1 ? options.assocs[0] : null;

    const sortableTH = (field, label, { className = '', ...props } = {}) => (
        <th {...props} className={`sortable ${className} ${params.order === field ? 'sorted' : ''}`} onClick={() => handleChangeOrder(field)}>
            {label} <OrderMarker field={field} order={params.order} direction={params.direction} />
        </th>
    );

    return (<Container>
        <Header>
            <div>
                <h4>Relatório de Fornecedores{userAssoc && <> · {userAssoc}</>}</h4>
                <small>
                    Dados de desmatamento atualizados em {lastUpdate || '-'}
                    {report && <> · anos até {report.maxYear}</>}
                </small>
            </div>
            <HeaderActions>
                <Button variant="outline-secondary" onClick={handleExport} disabled={exporting}>
                    {exporting ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faDownload} />} Exportar Excel
                </Button>
            </HeaderActions>
        </Header>

        {error && <Alert variant="warning">{error}</Alert>}

        {summary && <SummaryRow>
            <SummaryCard title="Fornecedores" value={numeral(summary.total).format('0,0')} />
            <SummaryCard title="Risco alto" value={numeral(summary.alto).format('0,0')} className="alto" />
            <SummaryCard title="Risco baixo" value={numeral(summary.baixo).format('0,0')} className="baixo" />
            <SummaryCard title={`Desmatamento ${summary.lastYear || ''} (ha)`} value={formatNumber(summary.desm_last_year)} />
        </SummaryRow>}

        <Panel>
            <Toolbar>
                <SearchBox>
                    <FontAwesomeIcon icon={faSearch} />
                    <input placeholder="Buscar fornecedor pelo nome ou CPF..." value={name} onChange={(e) => _name(e.target.value)} onKeyUp={handleNameKey} />
                    {!!params.filters.name && <ClearButton onClick={clearName} title="Limpar"><FontAwesomeIcon icon={faTimes} /></ClearButton>}
                </SearchBox>

                <ToolbarFilters>
                    {isADM && <FilterSelect value={params.filters.assoc || ''} onChange={(e) => handleFilter('assoc', e.target.value || false)}>
                        <option value="">Todas as associações</option>
                        {options.assocs.map((assoc) => <option key={assoc} value={assoc}>{assoc}</option>)}
                    </FilterSelect>}

                    <FilterSelect value={params.filters.micro || ''} onChange={(e) => handleFilter('micro', e.target.value || false)}>
                        <option value="">Todas as microrregiões</option>
                        {options.micros.map((micro) => <option key={micro} value={micro}>{micro}</option>)}
                    </FilterSelect>

                    <FilterSelect value={params.filters.risco || ''} onChange={(e) => handleFilter('risco', e.target.value || false)}>
                        <option value="">Todos os riscos</option>
                        <option value="ALTO">Risco alto</option>
                        <option value="BAIXO">Risco baixo</option>
                        <option value="sem">Sem risco</option>
                    </FilterSelect>

                    <YearColumnsPicker
                        years={years}
                        hiddenYears={hiddenYears}
                        onToggle={toggleYear}
                        onShowAll={() => updateHiddenYears(hiddenYears.filter((ano) => !years.includes(ano)))}
                        onHideAll={() => updateHiddenYears([...new Set([...hiddenYears, ...years])])}
                    />
                </ToolbarFilters>
            </Toolbar>

            <TableScroll>
                <ReportTable>
                        <thead>
                            <tr>
                                {sortableTH('nome', 'Nome', { rowSpan: 2, className: 'sticky' })}
                                <th rowSpan={2}>CPF</th>
                                <th rowSpan={2}>Seringal</th>
                                <th rowSpan={2}>Colocação</th>
                                {sortableTH('municipio', 'Município', { rowSpan: 2 })}
                                {sortableTH('associacao', 'Associação', { rowSpan: 2 })}
                                {sortableTH('microrregiao', 'Microrregião', { rowSpan: 2 })}
                                {sortableTH('risco', 'Risco', { rowSpan: 2 })}
                                <th className="group" colSpan={visibleYears.length + 1}>Desmatamento (ha)</th>
                            </tr>
                            <tr>
                                {visibleYears.map((ano) => <React.Fragment key={ano}>{sortableTH(`desm_${ano}`, ano, { className: 'number' })}</React.Fragment>)}
                                {sortableTH('desm_3y', riskWindow ? `Últimos 3 anos (${riskWindow.start}–${riskWindow.end})` : 'Últimos 3 anos', { className: 'number' })}
                            </tr>
                        </thead>
                        <tbody>
                            {!report && !error && <tr><CenteredTD colSpan={9 + visibleYears.length}>Carregando...</CenteredTD></tr>}
                            {report && report.rows.length === 0 && <tr><CenteredTD colSpan={9 + visibleYears.length}>Nenhum fornecedor encontrado!</CenteredTD></tr>}
                            {report && report.rows.map((row) => <tr key={row.prodId}>
                                <td className="sticky"><span className="name">{row.nome}</span></td>
                                <td>{row.cpf ? <Chip>{row.cpf}</Chip> : '-'}</td>
                                <td>{row.seringal || '-'}</td>
                                <td>{row.colocacao || '-'}</td>
                                <td>{row.municipio || '-'}</td>
                                <td>{row.associacao || '-'}</td>
                                <td>{row.microrregiao || '-'}</td>
                                <td><RiscoDot risco={row.risco} /></td>
                                {visibleYears.map((ano) => <NumberTD key={ano} value={row.desm[ano]} />)}
                                <NumberTD value={row.desm_3y} className="total" />
                            </tr>)}
                        </tbody>
                </ReportTable>
            </TableScroll>

            <Pager
                currentPage={params.page}
                paginatorData={report && { pages: report.pages, hasPrevious: report.hasPrevious, hasNext: report.hasNext }}
                onChangePage={handlePageChange}
            />
        </Panel>
    </Container>);
}

function YearColumnsPicker({ years, hiddenYears, onToggle, onShowAll, onHideAll }) {
    const hiddenCount = years.filter((ano) => hiddenYears.includes(ano)).length;

    return <Dropdown alignRight>
        <Dropdown.Toggle as={GhostButton} disabled={!years.length}>
            <FontAwesomeIcon icon={faColumns} /> Anos {hiddenCount > 0 && `(${years.length - hiddenCount}/${years.length})`}
        </Dropdown.Toggle>
        <YearsMenu>
            <div className="actions">
                <Button variant="link" size="sm" onClick={onShowAll}>Todos</Button>
                <Button variant="link" size="sm" onClick={onHideAll}>Nenhum</Button>
            </div>
            <div className="years">
                {years.map((ano) => <Form.Check
                    key={ano}
                    id={`report-year-${ano}`}
                    type="checkbox"
                    label={ano}
                    checked={!hiddenYears.includes(ano)}
                    onChange={() => onToggle(ano)}
                />)}
            </div>
        </YearsMenu>
    </Dropdown>;
}

function SummaryCard({ title, value, className = '' }) {
    return <StatCard className={className}>
        <div className="title">{title}</div>
        <div className="value">{value}</div>
    </StatCard>;
}

function RiscoDot({ risco }) {
    if (risco === 'ALTO') return <StatusDot className="danger">Alto</StatusDot>;
    if (risco === 'BAIXO') return <StatusDot className="success">Baixo</StatusDot>;
    return <span className="muted">-</span>;
}

function NumberTD({ value, className = '' }) {
    return <td className={`number ${!value ? 'zero' : ''} ${className}`}>{formatNumber(value)}</td>;
}

function OrderMarker({ field, order, direction }) {
    if (field !== order) return <></>;

    return <OMarker><FontAwesomeIcon icon={direction === 'asc' ? faArrowDown : faArrowUp} /></OMarker>;
}

/* Styles */

const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
`;

const StatCard = styled.div`
    background: #fff;
    border: 1px solid var(--vft-border);
    border-radius: 10px;
    padding: 14px 16px;

    .title {
        font-size: 13px;
        color: var(--vft-muted);
    }

    .value {
        margin-top: 2px;
        font-size: 24px;
        font-weight: 600;
        color: var(--vft-ink);
    }

    &.alto .value {
        color: var(--vft-accent);
    }

    &.baixo .value {
        color: var(--vft-success);
    }
`;

const YearsMenu = styled(Dropdown.Menu)`
    padding: 8px 12px;
    min-width: 240px;
    border: none;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(17, 24, 39, .12);

    .actions {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid var(--vft-border);
        margin-bottom: 6px;
        padding-bottom: 4px;
    }

    .actions .btn {
        padding: 0;
        color: var(--vft-ink);
    }

    .years {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 4px 12px;
        max-height: 300px;
        overflow-y: auto;
        font-size: 14px;
    }

    .form-check-input {
        accent-color: var(--vft-ink);
    }
`;

// Tabela padrão das telas de gestão, adaptada ao cabeçalho em duas linhas e à 1ª coluna fixa
const ReportTable = styled(ListTable)`
    font-size: 13px;

    th, td {
        padding: 10px 12px;
        white-space: nowrap;
    }

    th, th:first-child, th:last-child {
        border-radius: 0;
    }

    thead tr:first-child th:first-child {
        border-radius: 8px 0 0 8px;
    }

    thead tr:first-child th:last-child {
        border-top-right-radius: 8px;
    }

    thead tr:last-child th:last-child {
        border-bottom-right-radius: 8px;
    }

    th.group {
        text-align: center;
        padding-bottom: 6px;
        border-bottom: 1px solid var(--vft-border);
    }

    th.sortable {
        cursor: pointer;
        user-select: none;
    }

    th.sortable:hover, th.sorted {
        color: var(--vft-ink);
    }

    th.sorted {
        font-weight: 600;
    }

    td.zero {
        color: var(--vft-border-strong);
    }

    td.total {
        font-weight: 600;
    }

    .sticky {
        position: sticky;
        left: 0;
        z-index: 1;
        max-width: 280px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    th.sticky {
        background-color: var(--vft-subtle);
    }

    td.sticky {
        background-color: #fff;
    }

    tbody tr:hover td.sticky {
        background-color: var(--vft-subtle);
    }
`;

const OMarker = styled.span`
    margin-left: 2px;
    font-size: 0.85em;
`;

export default SupplierReport;
